
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';
import { RootState } from './store';

interface Room {
  id: number;
  name: string;
  LocalId: number;
  description?: string;
  surface?: number;
  photosroom?: Array<{ url: string }>;
}

interface PictureRoom {
  id: number;
  url: string;
  RoomId: number;
}

interface RoomState {
  rooms: Room[];
  room: Room[];
  activeRoomId: number | null;
  picturerooms: PictureRoom[];
  loading: boolean;
  error: string | null;
}

const initialState: RoomState = {
  rooms: [],
  room: [],
  activeRoomId: null,
  picturerooms: [],
  loading: false,
  error: null,
};

export const fetchRooms = createAsyncThunk<Room[], void, { state: RootState }>(
  'room/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/rooms`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rooms');
    }
  }
);

export const fetchRoom = createAsyncThunk<Room[], number, { state: RootState }>(
  'room/fetchByLocal',
  async (localId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/rooms/local/${localId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRooms());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch room');
    }
  }
);

export const fetchPictureRoom = createAsyncThunk<PictureRoom[], number, { state: RootState }>(
  'room/fetchPictures',
  async (roomId, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/picturerooms/${roomId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch room pictures');
    }
  }
);

export const createRoom = createAsyncThunk<Room, Partial<Room>, { state: RootState }>(
  'room/create',
  async (roomData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/rooms`, roomData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRooms());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create room');
    }
  }
);

export const updateRoom = createAsyncThunk<
  Room,
  { roomData: Partial<Room>; roomId: number },
  { state: RootState }
>(
  'room/update',
  async ({ roomData, roomId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/rooms/${roomId}`, roomData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRooms());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update room');
    }
  }
);

const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    encoursRoom: (state, action) => {
      state.activeRoomId = action.payload;
    },
    clearRoom: (state) => {
      state.room = [];
    },
    clearRooms: (state) => {
      state.rooms = [];
    },
    resetRoomState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRooms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.loading = false;
        state.rooms = action.payload;
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.room = action.payload;
      })
      .addCase(fetchRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchPictureRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPictureRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.picturerooms = action.payload;
      })
      .addCase(fetchPictureRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.rooms.push(action.payload);
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRoom.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rooms.findIndex((room) => room.id === action.payload.id);
        if (index !== -1) {
          state.rooms[index] = action.payload;
        }
      })
      .addCase(updateRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { encoursRoom, clearRoom, clearRooms, resetRoomState } = roomSlice.actions;
export default roomSlice.reducer;
